import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Select,
  Space,
  Tag,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Input,
  Image,
  Descriptions,
  Breadcrumb,
  Progress,
  message,
  Alert,
  Tooltip,
  Upload,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  RollbackOutlined,
  UploadOutlined,
  SubnodeOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useParams, Link } from 'react-router-dom';
import { Episode, EpisodeSubtitleInfo } from '../../types';
import { episodesApi, storiesApi, CreateEpisodeRequest, UpdateEpisodeRequest } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, getStatusColor } from '../../utils';
import { EPISODE_STATUS, STORY_STATUS, GENDER } from '../../constants';
import { ImageUpload } from '../../components/ImageUpload';
import { VideoUpload } from '../../components/VideoUpload';
import { EpisodeSubtitleAiPanel } from '../../components/EpisodeSubtitleAiPanel';
import { describeSubtitleState } from '../../utils/episodePublishGate';

const { Title } = Typography;
const { Option } = Select;

const StoryDetail: React.FC = () => {
  const { id: storyId } = useParams<{ id: string }>();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<Episode | null>(null);
  const [form] = Form.useForm();
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Epic 8: Subtitle state
  // pendingSubtitleUrls: danh sách URL entries đang nhập (chưa save), hỗ trợ nhiều ngôn ngữ
  const [pendingSubtitleUrls, setPendingSubtitleUrls] = useState<
    Array<{ id: number; language: string; label: string; url: string }>
  >([]);
  const pendingSubtitleIdRef = useRef(0);
  // File upload (edit only): một file mỗi lần
  const [subtitleFile, setSubtitleFile] = useState<File | null>(null);
  const [subtitleFileLanguage, setSubtitleFileLanguage] = useState('vi');
  const [subtitleFileLabel, setSubtitleFileLabel] = useState('Tiếng Việt');
  const [subtitleUploading, setSubtitleUploading] = useState(false);
  const [episodeList, setEpisodeList] = useState<Episode[]>([]);
  const [orderChanged, setOrderChanged] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<{
    [episodeId: string]: {
      status: string;
      progress: number;
      errorMessage?: string;
      jobId?: string;
    }
  }>({});
  const [pollingEpisodes, setPollingEpisodes] = useState<Set<string>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const dragEpisodeIdRef = useRef<string | null>(null);

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch story detail
  const {
    data: storyData,
    loading: storyLoading,
  } = useApi(
    useCallback(() => {
      if (!storyId) return Promise.reject(new Error('Story ID is required'));
      return storiesApi.getStoryDetail(storyId);
    }, [storyId]),
    { immediate: true }
  );

  // Fetch episodes
  const getEpisodes = useCallback(() => {
    if (!storyId) {
      return Promise.resolve({ data: [], totalRecord: 0 });
    }
    return episodesApi.getEpisodes(storyId, {
      pageIndex: 0,
      pageSize: 1000, // fetch all episodes for reordering
      status: statusFilter || undefined,
    });
  }, [storyId, statusFilter]);

  const {
    data: episodesData,
    loading: episodesLoading,
    refresh: refreshEpisodes,
  } = useApi(getEpisodes, { immediate: true });

  // Tự động fetch khi filter hoặc trang thay đổi
  useEffect(() => {
    refreshEpisodes();
  }, [refreshEpisodes, statusFilter]);

  // Đồng bộ danh sách episode để drag-drop
  useEffect(() => {
    if (episodesData?.data) {
      setEpisodeList(episodesData.data);
      setOrderChanged(false);
    }
  }, [episodesData]);

  // Auto-refresh khi có episode đang uploading hoặc processing
  useEffect(() => {
    const hasUploadingOrProcessing = episodeList.some(
      ep => ep.status === EPISODE_STATUS.UPLOADING || ep.status === EPISODE_STATUS.PROCESSING
    );

    if (hasUploadingOrProcessing) {
      const interval = setInterval(() => {
        refreshEpisodes();
      }, 10000); // Refresh mỗi 5 giây

      return () => clearInterval(interval);
    }
  }, [episodeList, refreshEpisodes]);

  // Polling video processing status
  const checkProcessingStatus = useCallback(async (episodeId: string) => {
    try {
      const result = await episodesApi.getProcessingStatus(episodeId);
      const statusData = result.data;

      setProcessingStatus(prev => ({
        ...prev,
        [episodeId]: {
          status: statusData.status,
          progress: statusData.progress,
          errorMessage: statusData.errorMessage,
          jobId: statusData.jobId,
        }
      }));

      // If processing completed or failed, stop polling
      if (statusData.status === 'completed' || statusData.status === 'failed') {
        setPollingEpisodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(episodeId);
          return newSet;
        });

        // Refresh episodes list to update status
        refreshEpisodes();

        if (statusData.status === 'completed') {
          message.success('Video processed successfully!');
        } else if (statusData.status === 'failed') {
          message.error(`Video processing error: ${statusData.errorMessage || 'Unknown error'}`);
        }
      }
    } catch (error) {
      // If job not found, stop polling
      setPollingEpisodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(episodeId);
        return newSet;
      });
    }
  }, [refreshEpisodes]);

  // Start polling for processing episodes
  useEffect(() => {
    if (pollingEpisodes.size > 0) {
      intervalRef.current = setInterval(() => {
        pollingEpisodes.forEach(episodeId => {
          checkProcessingStatus(episodeId);
        });
      }, 10000); // Check every 3 seconds
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [pollingEpisodes, checkProcessingStatus]);

  // Check processing status for episodes with 'processing' status
  useEffect(() => {
    if (episodesData?.data) {
      const processingEpisodes = episodesData.data.filter(ep => ep.status === 'processing');
      if (processingEpisodes.length > 0) {
        const newPollingSet = new Set<string>();
        processingEpisodes.forEach(ep => {
          newPollingSet.add(ep.id);
          checkProcessingStatus(ep.id);
        });
        setPollingEpisodes(newPollingSet);
      }
    }
  }, [episodesData, checkProcessingStatus]);

  const handleFilterChange = useCallback(() => {
    refreshEpisodes();
  }, [refreshEpisodes]);

  const handleCreate = () => {
    setEditingEpisode(null);
    form.resetFields();
    setThumbnailFile(null);
    setVideoFile(null);
    setVideoUrlInput('');
    setUploadProgress(0);
    setPendingSubtitleUrls([]);
    setSubtitleFile(null);
    setSubtitleFileLanguage('en');
    setSubtitleFileLabel('English');
    setIsModalVisible(true);
  };

  const handleEdit = (episode: Episode) => {
    setEditingEpisode(episode);
    form.setFieldsValue({
      isLocked: episode.isLocked,
    });
    setThumbnailFile(null);
    setVideoFile(null);
    setVideoUrlInput(episode.videoUrl || '');
    setUploadProgress(0);
    setPendingSubtitleUrls([]);
    setSubtitleFile(null);
    setSubtitleFileLanguage('en');
    setSubtitleFileLabel('English');
    setIsModalVisible(true);
  };

  const SUBTITLE_LANGUAGE_LABELS: Record<string, string> = {
    vi: 'Tiếng Việt', en: 'English', zh: '中文',
    ja: '日本語', ko: '한국어', th: 'ภาษาไทย',
  };

  // Epic 8: Thêm 1 pending URL row
  const handleAddSubtitleRow = () => {
    const id = ++pendingSubtitleIdRef.current;
    setPendingSubtitleUrls(prev => [
      ...prev,
      { id, language: 'en', label: 'English', url: '' },
    ]);
  };

  // Epic 8: Save tất cả pending URL rows (edit mode)
  const handleSaveAllPendingUrls = async () => {
    if (!storyId || !editingEpisode) return;
    const valid = pendingSubtitleUrls.filter(e => e.url.trim());
    if (!valid.length) return;
    try {
      setSubtitleUploading(true);
      let updated = editingEpisode;
      for (const entry of valid) {
        const result = await episodesApi.setSubtitleUrl(
          storyId, editingEpisode.id, entry.language, entry.label, entry.url.trim()
        );
        updated = result.data;
      }
      message.success(`Saved ${valid.length} subtitle(s)!`);
      setPendingSubtitleUrls([]);
      setEditingEpisode(updated);
      refreshEpisodes();
    } catch {
      message.error('Failed to save subtitle URLs!');
    } finally {
      setSubtitleUploading(false);
    }
  };

  // Epic 8: Upload 1 subtitle file (edit mode)
  const handleUploadSubtitleFile = async () => {
    if (!storyId || !editingEpisode || !subtitleFile) return;
    try {
      setSubtitleUploading(true);
      const result = await episodesApi.uploadSubtitle(
        storyId, editingEpisode.id, subtitleFile, subtitleFileLanguage, subtitleFileLabel
      );
      message.success(`Subtitle "${subtitleFileLabel}" uploaded!`);
      setSubtitleFile(null);
      setEditingEpisode(result.data);
      refreshEpisodes();
    } catch {
      message.error('Failed to upload subtitle file!');
    } finally {
      setSubtitleUploading(false);
    }
  };

  // Epic 8: Delete subtitle for a language
  const handleDeleteSubtitle = async (language: string) => {
    if (!storyId || !editingEpisode) return;
    try {
      await episodesApi.deleteSubtitle(storyId, editingEpisode.id, language);
      message.success(`Subtitle "${language}" deleted!`);
      const updated = {
        ...editingEpisode,
        subtitles: Object.fromEntries(
          Object.entries(editingEpisode.subtitles || {}).filter(([k]) => k !== language)
        ),
      };
      setEditingEpisode(updated);
      refreshEpisodes();
    } catch {
      message.error('Failed to delete subtitle!');
    }
  };

  const handleDelete = async (episode: Episode) => {
    if (!storyId) return;
    await executeAsync(
      () => episodesApi.deleteEpisode(storyId, episode.id),
      { successMessage: 'Episode deleted successfully!' }
    );
    refreshEpisodes();
  };

  const handleTriggerProcessing = async (episode: Episode) => {
    if (!storyId) return;

    // Check conditions before triggering
    if (episode.status === 'draft' && !episode.videoUrl) {
      message.error('Draft episode needs video URL to switch to Processing!');
      return;
    }

    try {
      const successMessage =
        episode.status === 'draft' ? 'Switched to video processing!' :
          episode.status === 'error' ? 'Retrying video processing!' :
            'Started video processing!';

      await executeAsync(
        () => episodesApi.triggerVideoProcessing(storyId, episode.id),
        { successMessage }
      );

      // Start polling for this episode
      setPollingEpisodes(prev => {
        const newSet = new Set(prev);
        newSet.add(episode.id);
        return newSet;
      });
      refreshEpisodes();
    } catch (error) {
      message.error('Cannot start video processing!');
    }
  };

  const handlePublishEpisode = async (episode: Episode) => {
    if (!storyId) return;

    try {
      await executeAsync(
        () => episodesApi.updateEpisode(storyId, episode.id, {
          status: EPISODE_STATUS.PUBLISHED
        } as UpdateEpisodeRequest),
        { successMessage: 'Episode published successfully!' }
      );
      refreshEpisodes();
    } catch (error: any) {
      // Hiện đúng lý do backend trả về (vd "Tập chưa có phụ đề nên chưa thể phát hành…"),
      // câu chung chung "Cannot publish episode" không giúp admin biết phải làm gì.
      message.error(error?.response?.data?.messages?.[0] || 'Cannot publish episode!');
    }
  };

  const handlePublishNow = async (episode: Episode) => {
    if (!storyId) return;

    try {
      await executeAsync(
        () => episodesApi.publishNow(storyId, episode.id, true),
        { successMessage: 'Episode published! Schedule recalculated.' }
      );
      refreshEpisodes();
    } catch (error) {
      message.error('Cannot publish episode!');
    }
  };

  const handleRevertEpisodeToReady = async (episode: Episode) => {
    if (!storyId) return;

    try {
      await executeAsync(
        () => episodesApi.updateEpisode(storyId, episode.id, {
          status: EPISODE_STATUS.READY
        } as UpdateEpisodeRequest),
        { successMessage: 'Episode reverted to Ready successfully!' }
      );
      refreshEpisodes();
    } catch (error) {
      message.error('Cannot revert episode to Ready!');
    }
  };

  const handleDragStart = useCallback((episodeId: string) => {
    dragEpisodeIdRef.current = episodeId;
  }, []);

  const handleDragEnter = useCallback((targetEpisodeId: string) => {
    const draggedId = dragEpisodeIdRef.current;
    if (!draggedId || draggedId === targetEpisodeId) return;

    setEpisodeList(prev => {
      const currentIndex = prev.findIndex(ep => ep.id === draggedId);
      const targetIndex = prev.findIndex(ep => ep.id === targetEpisodeId);
      if (currentIndex === -1 || targetIndex === -1) return prev;

      const updated = [...prev];
      const [moved] = updated.splice(currentIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return updated;
    });
    setOrderChanged(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragEpisodeIdRef.current = null;
  }, []);

  const handleResetOrder = useCallback(() => {
    if (episodesData?.data) {
      setEpisodeList(episodesData.data);
      setOrderChanged(false);
    }
  }, [episodesData]);

  const handleSaveOrder = useCallback(async () => {
    if (!storyId || episodeList.length === 0) return;
    try {
      setReorderLoading(true);
      // List is shown chap desc (newest on top); reorder API expects reading order chap 1..n (oldest first)
      await executeAsync(
        () =>
          episodesApi.reorderEpisodes(
            storyId,
            [...episodeList].reverse().map((ep) => ep.id)
          ),
        { successMessage: 'Cập nhật thứ tự chap thành công!' }
      );
      setOrderChanged(false);
      refreshEpisodes();
    } catch (error) {
      message.error('Không thể cập nhật thứ tự chap');
    } finally {
      setReorderLoading(false);
    }
  }, [storyId, episodeList, executeAsync, refreshEpisodes]);

  const handleSubmit = async (values: CreateEpisodeRequest) => {
    if (!storyId) return;

    try {
      setUploadProgress(0);

      // Merge videoUrlInput nếu không có file (paste URL)
      if (!videoFile && videoUrlInput.trim()) {
        values.videoUrl = videoUrlInput.trim();
      }

      // Merge pending subtitle URLs khi create (include nhiều language cùng lúc)
      if (!editingEpisode && pendingSubtitleUrls.length > 0) {
        const validEntries = pendingSubtitleUrls.filter(e => e.url.trim());
        if (validEntries.length > 0) {
          (values as any).subtitles = Object.fromEntries(
            validEntries.map(e => [e.language, { url: e.url.trim(), label: e.label }])
          );
        }
      }

      // Không set status nữa - để backend tự động xử lý
      // Backend sẽ set: uploading -> processing -> ready
      if (!editingEpisode && !videoFile) {
        values.status = 'draft'; // Chỉ set draft khi tạo mới không có video
      }

      const operation = editingEpisode
        ? () => episodesApi.updateEpisode(
          storyId,
          editingEpisode.id,
          values as UpdateEpisodeRequest,
          thumbnailFile || undefined,
          videoFile || undefined,
          (progress) => setUploadProgress(progress)
        )
        : () => episodesApi.createEpisode(
          storyId,
          values,
          thumbnailFile || undefined,
          videoFile || undefined,
          (progress) => setUploadProgress(progress)
        );

      const result = await executeAsync(
        operation,
        {
          successMessage: videoFile
            ? (editingEpisode
              ? 'Episode updated! Video is being uploaded in background.'
              : 'Episode created! Video is being uploaded in background.')
            : (editingEpisode
              ? 'Episode updated successfully!'
              : 'Episode created successfully!')
        }
      );

      if (result) {
        // If video is uploaded, start polling for status updates
        if (videoFile) {
          const episodeId = editingEpisode?.id || result.data.id;
          if (episodeId) {
            setPollingEpisodes(prev => {
              const newSet = new Set(prev);
              newSet.add(episodeId);
              return newSet;
            });

            // Show info message
            message.info('Video is being uploaded. Status will update automatically.', 5);
          }
        }

        setIsModalVisible(false);
        form.resetFields();
        setThumbnailFile(null);
        setVideoFile(null);
        setVideoUrlInput('');
        setUploadProgress(0);
        setPendingSubtitleUrls([]);
        setSubtitleFile(null);
        refreshEpisodes();
      }
    } catch (error) {
      setUploadProgress(0);
      message.error('Upload error occurred!');
    }
  };

  const columns = [
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 80,
      render: (url: string) => (
        url ? (
          <Image
            width={60}
            height={40}
            src={url}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
        ) : (
          <div style={{
            width: 60,
            height: 40,
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            color: '#999'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Episode',
      dataIndex: 'chap',
      key: 'chap',
      width: 80,
    },
    {
      title: 'Video',
      dataIndex: 'videoUrl',
      key: 'videoUrl',
      width: 100,
      render: (url: string) => (
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button type="link" size="small">
              View video
            </Button>
          </a>
        ) : (
          <span style={{ color: '#999' }}>Not available</span>
        )
      ),
    },
    {
      title: 'Subtitles',
      dataIndex: 'subtitles',
      key: 'subtitles',
      width: 150,
      render: (_: any, record: Episode) => {
        // 260810 E3-P2: backend giữ lại tập thiếu phụ đề trong im lặng — chỗ này phải nói ra,
        // nếu không đội content sẽ tưởng hệ thống hỏng khi thấy tập mãi không lên sóng.
        const state = describeSubtitleState(record.subtitles, record.status);

        return (
          <Space direction="vertical" size={2}>
            {state.languages.length === 0 ? (
              <span style={{ color: '#bbb', fontSize: 12 }}>—</span>
            ) : (
              <Space wrap size={4}>
                {state.languages.map((lang) => (
                  <Tooltip key={lang} title={(record.subtitles as any)?.[lang]?.url}>
                    <Tag color="cyan" style={{ cursor: 'pointer', fontSize: 11 }}>
                      {lang.toUpperCase()}
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            )}
            {state.blocking && (
              <Tooltip title={state.hint}>
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  ⏸ Chờ phụ đề
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Episode) => {
        const processStatus = processingStatus[record.id];

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Tag color={getStatusColor(status)}>
              {status === EPISODE_STATUS.DRAFT && 'Draft'}
              {status === EPISODE_STATUS.UPLOADING && '⏫ Uploading'}
              {status === EPISODE_STATUS.PROCESSING && '⚙️ Processing'}
              {status === EPISODE_STATUS.READY && 'Ready'}
              {status === EPISODE_STATUS.SCHEDULED && 'Scheduled'}
              {status === EPISODE_STATUS.PUBLISHED && 'Published'}
              {status === EPISODE_STATUS.ERROR && 'Error'}
              {status === EPISODE_STATUS.FAILED && '❌ Failed'}
            </Tag>
            {status === EPISODE_STATUS.SCHEDULED && record.publishedAt && (
              <div style={{ fontSize: 11, color: '#2f54eb', marginTop: 2 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {new Date(record.publishedAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                })}
              </div>
            )}

            {/* Display upload/processing status */}
            {status === 'uploading' && (
              <div style={{ width: '100%' }}>
                <Progress
                  percent={0}
                  size="small"
                  status="active"
                  showInfo={false}
                />
                <div style={{ fontSize: 11, color: '#1890ff', marginTop: 2 }}>
                  Uploading video to server...
                </div>
              </div>
            )}

            {/* Display video processing information */}
            {status === 'processing' && processStatus && (
              <div style={{ width: '100%' }}>
                <Progress
                  percent={processStatus.progress}
                  size="small"
                  status={processStatus.status === 'FAILED' ? 'exception' : 'active'}
                  format={() => `${processStatus.progress}%`}
                />
                <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                  {processStatus.status === 'PENDING' && 'Waiting for processing...'}
                  {processStatus.status === 'PROCESSING' && 'Processing video...'}
                  {processStatus.status === 'FAILED' && (
                    <Tooltip title={processStatus.errorMessage}>
                      <span style={{ color: '#ff4d4f' }}>Processing error</span>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Locked',
      dataIndex: 'isLocked',
      key: 'isLocked',
      render: (isLocked: boolean) => (
        <Tag color={isLocked ? 'red' : 'green'}>
          {isLocked ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Published At',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (date: string | null) => date
        ? new Date(date).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })
        : <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Episode) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />

          {/* Trigger video processing button - only show with appropriate conditions */}
          {(() => {
            const canTriggerProcessing =
              record.videoUrl &&
              record.status !== 'processing' &&
              record.status !== 'ready' &&                         // Ready cannot be reprocessed
              (
                (record.status === 'draft' && record.videoUrl) ||  // Draft has video URL
                record.status === 'error'                         // Failed can retry
              );

            return canTriggerProcessing && (
              <Tooltip title={
                record.status === 'draft' ? 'Switch to video processing' :
                  record.status === 'error' ? 'Retry video processing' :
                    'Start video processing'
              }>
                <Button
                  type="text"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleTriggerProcessing(record)}
                  loading={asyncLoading}
                  style={{ color: '#1890ff' }}
                />
              </Tooltip>
            );
          })()}

          {/* Refresh processing status button */}
          {record.status === 'processing' && (
            <Tooltip title="Refresh status">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => checkProcessingStatus(record.id)}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          )}

          {/* Publish button for Ready episode */}
          {record.status === 'ready' && (() => {
            // 260810 E3-P2: backend từ chối publish khi thiếu phụ đề — chặn sớm ở đây để admin
            // không bấm rồi mới nhận lỗi, và nói rõ vì sao nút bị mờ.
            const gate = describeSubtitleState(record.subtitles, record.status);
            return (
              <Tooltip title={gate.blocking ? gate.hint : 'Publish episode'}>
                <Button
                  type="text"
                  onClick={() => handlePublishEpisode(record)}
                  loading={asyncLoading}
                  disabled={gate.blocking}
                  style={gate.blocking ? undefined : { color: '#52c41a', fontWeight: 'bold' }}
                >
                  Publish
                </Button>
              </Tooltip>
            );
          })()}

          {/* Publish Now button for Scheduled episode */}
          {record.status === 'scheduled' && (
            <Popconfirm
              title="Publish this episode now?"
              description={`This episode is scheduled for ${record.publishedAt ? new Date(record.publishedAt).toLocaleString() : 'later'}. Publishing now will recalculate the schedule for remaining episodes.`}
              onConfirm={() => handlePublishNow(record)}
              okText="Publish Now"
              cancelText="Cancel"
            >
              <Tooltip title="Publish now (skip schedule)">
                <Button
                  type="text"
                  icon={<ThunderboltOutlined />}
                  loading={asyncLoading}
                  style={{ color: '#faad14', fontWeight: 'bold' }}
                >
                  Publish Now
                </Button>
              </Tooltip>
            </Popconfirm>
          )}

          {/* Revert to Ready button for Published episode */}
          {record.status === 'published' && (
            <Popconfirm
              title="Revert episode to Ready?"
              description="This will change the episode status from Published to Ready."
              onConfirm={() => handleRevertEpisodeToReady(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<RollbackOutlined />}
                title="Revert to Ready"
              />
            </Popconfirm>
          )}

          <Popconfirm
            title="Are you sure you want to delete this episode?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const story = storyData?.data;

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        <Breadcrumb.Item style={{ display: 'inline-flex', alignItems: 'center', padding: 0 }}>
          {/* Tinh chỉnh: giảm gap, cân chỉnh kích thước icon và text để căn giữa chính xác */}
          <Link
            to="/stories"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: 0,
              cursor: 'pointer',
              color: 'rgba(0,0,0,0.85)',
              textDecoration: 'none',
              fontSize: 14,
              lineHeight: 1
            }}
          >
            <ArrowLeftOutlined style={{ fontSize: 14, lineHeight: 1, transform: 'translateY(-1px)' }} />
            <span style={{ display: 'inline-block', lineHeight: 1, fontSize: 14 }}>Stories List</span>
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item style={{ display: 'inline-flex', alignItems: 'center', fontSize: 14 }}>{story?.title || 'Story Detail'}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Story Information */}
      <Card loading={storyLoading} style={{ marginBottom: 24 }}>
        {story && (
          <>
            <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
              {story.coverUrl && (
                <Image
                  width={200}
                  height={150}
                  src={story.coverUrl}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              )}
              <div style={{ flex: 1 }}>
                <Title level={2} style={{ marginTop: 0 }}>
                  {story.title}
                </Title>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(story.status)}>
                      {story.status === STORY_STATUS.DRAFT && 'Draft'}
                      {story.status === STORY_STATUS.PUBLISHED && 'Published'}
                      {story.status === STORY_STATUS.ARCHIVED && 'Archived'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Gender Target">
                    <Tag color={story.gender === GENDER.MALE ? 'blue' : story.gender === GENDER.FEMALE ? 'pink' : 'default'}>
                      {story.gender === GENDER.MALE ? 'Male' : story.gender === GENDER.FEMALE ? 'Female' : 'Unisex'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Episode status">
                    <Tag color={story.ongoing ? 'orange' : 'default'}>
                      {story.ongoing ? 'Ongoing' : 'Completed'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created Date">
                    {formatDate(story.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Episodes">
                    {episodesData?.totalRecord || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Publish Frequency">
                    {story.publishFrequency
                      ? <Tag color="blue">{story.publishFrequency} ep/day (auto)</Tag>
                      : <Tag>Manual</Tag>
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label="Description" span={2}>
                    {story.description}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Episodes List */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Episodes List
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create New Episode
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Select
            placeholder="Filter by status"
            allowClear
            style={{ width: 200 }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value || '');
              handleFilterChange();
            }}
          >
            <Option value={EPISODE_STATUS.DRAFT}>Draft</Option>
            <Option value={EPISODE_STATUS.UPLOADING}>Uploading</Option>
            <Option value={EPISODE_STATUS.PROCESSING}>Processing</Option>
            <Option value={EPISODE_STATUS.READY}>Ready</Option>
            <Option value={EPISODE_STATUS.PUBLISHED}>Published</Option>
            <Option value={EPISODE_STATUS.ERROR}>Error</Option>
            <Option value={EPISODE_STATUS.FAILED}>Failed</Option>
          </Select>

          <Space>
            <Button
              onClick={handleResetOrder}
              disabled={!orderChanged || reorderLoading}
            >
              Reset order
            </Button>
            <Button
              type="primary"
              onClick={handleSaveOrder}
              disabled={!orderChanged}
              loading={reorderLoading}
            >
              Change order
            </Button>
          </Space>
        </div>


        <Table
          columns={columns}
          dataSource={episodeList}
          rowKey="id"
          loading={reorderLoading}
          pagination={false}
          onRow={(record) => ({
            draggable: true,
            style: { cursor: 'move' },
            onDragStart: () => handleDragStart(record.id),
            onDragEnter: () => handleDragEnter(record.id),
            onDragOver: (event) => event.preventDefault(),
            onDragEnd: handleDragEnd,
            onDrop: handleDragEnd,
          })}
        />
      </Card>

      {/* Create/Edit Episode Modal */}
      <Modal
        title={editingEpisode ? 'Edit Episode' : 'Create New Episode'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setThumbnailFile(null);
          setVideoFile(null);
          setVideoUrlInput('');
          setUploadProgress(0);
          setPendingSubtitleUrls([]);
          setSubtitleFile(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >

          <Form.Item
            label="Thumbnail"
          >
            <ImageUpload
              value={thumbnailFile ?? editingEpisode?.thumbnailUrl}
              onChange={(value) => {
                if (value instanceof File) {
                  // Select new thumbnail
                  setThumbnailFile(value);
                } else if (value === undefined) {
                  // Delete thumbnail
                  setThumbnailFile(null);
                  if (editingEpisode) {
                    // Prevent falling back to old URL in edit mode
                    setEditingEpisode({
                      ...editingEpisode,
                      thumbnailUrl: undefined,
                    });
                  }
                } else {
                  setThumbnailFile(null);
                }
              }}
              disabled={asyncLoading}
            />
          </Form.Item>

          <Form.Item label="Video">
            {editingEpisode?.status === EPISODE_STATUS.PUBLISHED ? (
              // Published → chỉ hiển thị URL hiện tại, không cho sửa video
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#d46b08', fontWeight: 500 }}>
                    🔒 Video bị khóa — Episode đang Published
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 6 }}>
                  Không thể thay đổi video khi đang Published. Hãy <b>Revert to Ready</b> trước.
                </div>
                {videoUrlInput && (
                  <a href={videoUrlInput} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                    View current video ↗
                  </a>
                )}
              </div>
            ) : (
              <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', borderRadius: 8, padding: '12px 14px' }}>
                {/* Option 1: Paste URL */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Option 1: Paste URL</div>
                  <Input
                    value={videoUrlInput}
                    onChange={(e) => {
                      setVideoUrlInput(e.target.value);
                      if (e.target.value) setVideoFile(null);
                    }}
                    placeholder="https://cdn.../master.m3u8 hoặc video.mp4"
                    allowClear
                    addonAfter={
                      videoUrlInput ? (
                        <a href={videoUrlInput} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                          View
                        </a>
                      ) : null
                    }
                  />
                  {videoUrlInput && videoUrlInput !== editingEpisode?.videoUrl && (
                    <div style={{ fontSize: 11, color: '#fa8c16', marginTop: 4 }}>
                      ⚠️ URL thay đổi → episode sẽ reset về <b>Processing</b> để encode HLS lại
                    </div>
                  )}
                </div>
                {/* Option 2: Upload file */}
                <div>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Option 2: Upload file</div>
                  <VideoUpload
                    value={videoFile ? undefined : undefined}
                    onChange={(file) => {
                      setVideoFile(file);
                      if (file) setVideoUrlInput('');
                    }}
                    disabled={asyncLoading}
                    progress={uploadProgress}
                  />
                </div>
              </div>
            )}
          </Form.Item>

          {/* Status change logic notification */}
          {videoFile && (
            <Alert
              message="Automatic video processing"
              description="When uploading a new video, status will automatically switch to 'Processing' and the video will be processed."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Episode status notification */}
          {editingEpisode && (
            <Alert
              message={`Current status: ${editingEpisode.status === 'draft' ? 'Draft' :
                editingEpisode.status === 'processing' ? 'Processing' :
                  editingEpisode.status === 'ready' ? 'Ready' :
                    editingEpisode.status === 'published' ? 'Published' :
                      editingEpisode.status === 'error' ? 'Error' : editingEpisode.status
                }`}
              description="Status will be managed automatically. Use action buttons in the list to change status."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Display current processing status if editing processing episode */}
          {editingEpisode && editingEpisode.status === 'processing' && processingStatus[editingEpisode.id] && (
            <Alert
              message="Video processing status"
              description={
                <div>
                  <Progress
                    percent={processingStatus[editingEpisode.id].progress}
                    size="small"
                    status={processingStatus[editingEpisode.id].status === 'FAILED' ? 'exception' : 'active'}
                  />
                  <div style={{ marginTop: 8 }}>
                    Status: {processingStatus[editingEpisode.id].status}
                    {processingStatus[editingEpisode.id].errorMessage && (
                      <div style={{ color: '#ff4d4f', fontSize: 12 }}>
                        Error: {processingStatus[editingEpisode.id].errorMessage}
                      </div>
                    )}
                  </div>
                </div>
              }
              type={processingStatus[editingEpisode.id].status === 'FAILED' ? 'error' : 'info'}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            name="isLocked"
            label="Locked"
          >
            <Select placeholder="Select...">
              <Option value={false}>No</Option>
              <Option value={true}>Yes</Option>
            </Select>
          </Form.Item>

          {/* 260810: Phụ đề AI — chỉ có nghĩa khi tập đã có video để nghe */}
          {editingEpisode && editingEpisode.videoUrl && (
            <EpisodeSubtitleAiPanel
              episodeId={editingEpisode.id}
              videoUrl={editingEpisode.videoUrl}
              onApproved={refreshEpisodes}
            />
          )}

          {/* Epic 8: Subtitle management */}
          <>
            <Divider orientation={"left" as any} style={{ fontSize: 13 }}>
              <SubnodeOutlined style={{ marginRight: 6 }} />
              Subtitles
            </Divider>

            {/* Saved subtitles (edit only) */}
            {editingEpisode && editingEpisode.subtitles && Object.keys(editingEpisode.subtitles).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {Object.entries(editingEpisode.subtitles).map(([lang, info]: [string, EpisodeSubtitleInfo]) => (
                  <div key={lang} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 10px', background: '#f0fdf8', border: '1px solid #b7ebd9',
                    borderRadius: 6, marginBottom: 5,
                  }}>
                    <Tag color="cyan" style={{ margin: 0 }}>{lang.toUpperCase()}</Tag>
                    <span style={{ fontSize: 13, flex: 1 }}>{info.label}</span>
                    <a href={info.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>View</a>
                    <Popconfirm title={`Delete "${info.label}"?`} onConfirm={() => handleDeleteSubtitle(lang)} okText="Yes" cancelText="No">
                      <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ))}
              </div>
            )}

            {/* Pending URL rows - có thể add nhiều language cùng lúc */}
            {pendingSubtitleUrls.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                {pendingSubtitleUrls.map((entry) => (
                  <div key={entry.id} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <Select
                      value={entry.language}
                      size="small"
                      style={{ width: 80 }}
                      onChange={(val) => setPendingSubtitleUrls(prev => prev.map(e =>
                        e.id === entry.id ? { ...e, language: val, label: SUBTITLE_LANGUAGE_LABELS[val] ?? val.toUpperCase() } : e
                      ))}
                    >
                      <Option value="vi">VI</Option>
                      <Option value="en">EN</Option>
                      <Option value="zh">ZH</Option>
                      <Option value="ja">JA</Option>
                      <Option value="ko">KO</Option>
                      <Option value="th">TH</Option>
                    </Select>
                    <Input
                      size="small"
                      value={entry.label}
                      placeholder="Label"
                      style={{ width: 110 }}
                      onChange={(e) => setPendingSubtitleUrls(prev => prev.map(r =>
                        r.id === entry.id ? { ...r, label: e.target.value } : r
                      ))}
                    />
                    <Input
                      size="small"
                      value={entry.url}
                      placeholder="https://cdn.../subtitle.vtt"
                      style={{ flex: 1 }}
                      onChange={(e) => setPendingSubtitleUrls(prev => prev.map(r =>
                        r.id === entry.id ? { ...r, url: e.target.value } : r
                      ))}
                    />
                    <Button size="small" danger type="text" icon={<DeleteOutlined />}
                      onClick={() => setPendingSubtitleUrls(prev => prev.filter(r => r.id !== entry.id))}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Actions row */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <Button size="small" icon={<PlusOutlined />} onClick={handleAddSubtitleRow}>
                Add subtitle URL
              </Button>
              {editingEpisode && pendingSubtitleUrls.some(e => e.url.trim()) && (
                <Button size="small" type="primary" loading={subtitleUploading} onClick={handleSaveAllPendingUrls}>
                  Save {pendingSubtitleUrls.filter(e => e.url.trim()).length} URL(s)
                </Button>
              )}
              {!editingEpisode && pendingSubtitleUrls.length > 0 && (
                <span style={{ fontSize: 12, color: '#888' }}>
                  Sẽ lưu khi nhấn Create
                </span>
              )}
            </div>

            {/* Upload .vtt file - edit only */}
            {editingEpisode && (
              <div style={{ padding: '10px 12px', background: '#fafafa', border: '1px dashed #d9d9d9', borderRadius: 6 }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Upload .vtt file</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Select
                    value={subtitleFileLanguage}
                    size="small"
                    style={{ width: 130 }}
                    onChange={(val) => {
                      setSubtitleFileLanguage(val);
                      setSubtitleFileLabel(SUBTITLE_LANGUAGE_LABELS[val] ?? val.toUpperCase());
                    }}
                  >
                    <Option value="vi">VI - Tiếng Việt</Option>
                    <Option value="en">EN - English</Option>
                    <Option value="zh">ZH - 中文</Option>
                    <Option value="ja">JA - 日本語</Option>
                    <Option value="ko">KO - 한국어</Option>
                    <Option value="th">TH - ภาษาไทย</Option>
                  </Select>
                  <Input
                    size="small"
                    value={subtitleFileLabel}
                    placeholder="Label"
                    style={{ width: 110 }}
                    onChange={(e) => setSubtitleFileLabel(e.target.value)}
                  />
                  <Upload
                    accept=".vtt,.srt"
                    beforeUpload={(file) => { setSubtitleFile(file); return false; }}
                    showUploadList={false}
                    maxCount={1}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {subtitleFile ? subtitleFile.name : 'Choose .vtt'}
                    </Button>
                  </Upload>
                  {subtitleFile && (
                    <>
                      <Button size="small" type="primary" loading={subtitleUploading} onClick={handleUploadSubtitleFile}>
                        Upload
                      </Button>
                      <Button size="small" onClick={() => setSubtitleFile(null)}>Clear</Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Form.Item>
              <Progress
                percent={uploadProgress}
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
                Uploading... {uploadProgress}%
              </div>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
                disabled={uploadProgress > 0 && uploadProgress < 100}
              >
                {uploadProgress > 0 && uploadProgress < 100
                  ? `Uploading... ${uploadProgress}%`
                  : (editingEpisode ? 'Update' : 'Create')
                }
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StoryDetail;
